import { useState } from 'react';
import {
  CaretRight,
  CheckCircle,
  DownloadSimple,
  LockKey,
  ShieldCheck,
  SignOut,
  Translate,
} from '@phosphor-icons/react';
import Avatar from '../../../shared/components/Avatar';
import PageHeader from '../../../shared/components/PageHeader';
import { LANGUAGE_NAMES } from '../../../shared/constants/languages';
import { InstallAppModal } from '../../pwa';
import SettingsRow from './Parts/SettingsRow';

type SettingsPageProps = {
  user: any;
  setView: (view: string) => void;
  onLogout: () => void;
  t: any;
};

export default function SettingsPage({ user, setView, onLogout, t }: SettingsPageProps) {
  const [installOpen, setInstallOpen] = useState(false);
  const rows = [
    {
      icon: CheckCircle,
      label: t.visitedPlaces,
      description: t.visitedSubtitle,
      view: 'visited',
    },
    {
      icon: Translate,
      label: t.language,
      description: LANGUAGE_NAMES[user.language || 'cs'],
      view: 'language',
    },
    {
      icon: LockKey,
      label: t.changePassword,
      description: t.changePasswordRow,
      view: 'password',
    },
    {
      icon: DownloadSimple,
      label: t.getTheApp,
      description: t.getTheAppRow,
      view: 'install',
    },
    ...(user.isAdmin ? [{
      icon: ShieldCheck,
      label: t.admin,
      description: t.usersEntries,
      view: 'admin',
      admin: true,
    }] : []),
  ];

  return (
    <PageHeader
      className="settings-page"
      eyebrow={t.personalise}
      title={t.settings}
      subtitle={t.settingsSubtitle}
    >
      <section className="settings-list settings-list-merged">
        <button
          type="button"
          className="settings-profile"
          onClick={() => setView('account')}
          aria-label={t.account}
        >
          <Avatar user={user} size={58} />
          <div><b>{user.firstName} {user.lastName}</b><span>@{user.username}</span></div>
          <CaretRight />
        </button>
        {rows.map((row) => (
          <SettingsRow
            key={row.view}
            row={row}
            onSelect={(view) => view === 'install' ? setInstallOpen(true) : setView(view)}
          />
        ))}
        <button className="logout" onClick={onLogout}>
          <span className="setting-icon"><SignOut /></span>
          <span><b>{t.logout}</b><small>{t.signOutDevice}</small></span>
          <CaretRight />
        </button>
      </section>
      <footer className="settings-footer">
        <p className="version">© 2026 {t.version}. | {t.allRightsReserved}</p>
        <div className="legal-links">
          <button type="button" onClick={() => setView('terms')}>{t.termsAndConditions}</button>
          <span>·</span>
          <button type="button" onClick={() => setView('privacy')}>{t.privacyPolicy}</button>
        </div>
        <p className="studio-credit">
          {t.createdByStudio}{' '}
          <a href="https://v-studio.cc" target="_blank" rel="noreferrer">V-Studio</a>
        </p>
      </footer>
      {installOpen && <InstallAppModal t={t} onClose={() => setInstallOpen(false)} />}
    </PageHeader>
  );
}
